// Turns bundle (and free-gift) rows in the "Mst: Wishlist Prime" drawer into "View product" rows.
//
// A bundle's own variant is priced $0 in admin: the real charge sits on the sub-product lines that
// pi-bundle.js adds from the product page, together with the _bundle_id properties that tie them to
// it. Added from the wishlist, it landed in the cart as a bare $0 line with nothing behind it. Free
// gifts are $0 for the same reason and are only ever meant to ride along with a paid product. So any
// wishlist row whose variant costs 0 is made view-only: the $0 price, quantity box and add to cart
// button are hidden and a link to the product page takes their place.
//
// The app renders the drawer with React and offers no per-product switch for this (its
// hide_cart_btn setting swaps the button on every row), so it is done from the outside:
// - Rows are matched by the ids the app gives them (#product_var<variantId>, a[data-handle]) and
//   marked with a data attribute; React leaves attributes and nodes it does not own alone.
// - The variant price comes from /products/<handle>.js, the same request the app makes per row to
//   check availability, so it is normally served from the browser cache.
// - window.addToCart / window.addAllToCart, the app's global cart helpers its buttons call at click
//   time, are wrapped so "Add all to cart" skips view-only variants and still adds the rest.
//
// The same row pass also adds a "Save x%" chip to sale rows (see decorateSale), as the product page
// shows next to a compare-at price.
if (!window.themeWishlistViewOnly) {
  window.themeWishlistViewOnly = true;

  const ROOT_ID = 'iwish-drawer-root';
  const ROW_SELECTOR = '.iwishProduct-row[id^="product_var"]';
  const VIEW_ONLY_ATTR = 'data-theme-view-only';
  const LINK_CLASS = 'iwish-view-product';
  const NOTE_CLASS = 'iwish-view-note';
  const SAVE_CLASS = 'iwish-save';
  const BLOCKED_MESSAGE = 'Bundles and free gifts are added from their product page.';

  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const productUrl = (handle) => `${rootUrl()}products/${encodeURIComponent(handle)}`;

  // One request per handle for the page's lifetime; a failed lookup resolves to null so the row
  // simply keeps the app's normal button.
  const productCache = new Map();
  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${productUrl(handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  // Settled verdicts by variant id, so the cart wrappers can answer without a request.
  const viewOnlyVariants = new Map();

  const isViewOnlyVariant = async (handle, variantId) => {
    const key = String(variantId);
    if (viewOnlyVariants.has(key)) return viewOnlyVariants.get(key);

    const product = await fetchProduct(handle);
    const variant = product && product.variants.find((item) => String(item.id) === key);
    const viewOnly = Boolean(variant) && Number(variant.price) === 0;

    viewOnlyVariants.set(key, viewOnly);
    return viewOnly;
  };

  const rowVariantId = (row) => row.id.replace('product_var', '');
  const rowHandle = (row) => {
    const link = row.querySelector('[data-handle]');
    return link ? link.getAttribute('data-handle') : '';
  };

  // Idempotent: React re-renders can drop the nodes added here, so every pass re-checks them.
  const decorateRow = (row, handle) => {
    row.setAttribute(VIEW_ONLY_ATTR, 'true');

    const priceRow = row.querySelector('.price_Container');
    const priceHost = priceRow && priceRow.closest('.iwish-row');
    if (priceHost && !priceHost.parentNode.querySelector(`.${NOTE_CLASS}`)) {
      const note = document.createElement('p');
      note.className = NOTE_CLASS;
      note.textContent = 'Select options on the product page';
      priceHost.parentNode.insertBefore(note, priceHost);
    }

    const buttonHost = row.querySelector('.iwish-cartQty');
    if (buttonHost && !buttonHost.querySelector(`.${LINK_CLASS}`)) {
      const link = document.createElement('a');
      link.className = LINK_CLASS;
      link.href = productUrl(handle);
      link.textContent = 'View product';
      // The row itself carries an onClick (category menu toggle); keep the link's click to itself.
      link.addEventListener('click', (event) => event.stopPropagation());
      buttonHost.appendChild(link);
    }
  };

  // "$1,299.95" -> 1299.95. A lone comma followed by exactly two digits is a decimal comma.
  const parseMoney = (text) => {
    let value = String(text).replace(/[^\d.,]/g, '');
    if (value.includes('.') || !/,\d{2}$/.test(value)) value = value.replace(/,/g, '');
    else value = value.replace(/,/g, '.');
    return parseFloat(value);
  };

  // Adds the product page's "Save x%" chip after a sale row's struck compare-at price, worked out
  // from the two amounts the app printed, so it always agrees with what the row shows.
  const decorateSale = (row) => {
    const prices = row.querySelector('.price_Container .price-regular');
    if (!prices || prices.querySelector(`.${SAVE_CLASS}`)) return;

    const compareNode = prices.querySelector('.strikeThrough');
    const priceNode = compareNode && [...prices.children].find((node) => node !== compareNode);
    if (!priceNode) return;

    const compare = parseMoney(compareNode.textContent);
    const price = parseMoney(priceNode.textContent);
    if (!(compare > price) || !(price >= 0)) return;

    const percent = Math.round(((compare - price) / compare) * 100);
    if (percent < 1) return;

    const chip = document.createElement('span');
    chip.className = SAVE_CLASS;
    chip.textContent = `Save ${percent}%`;
    prices.appendChild(chip);
  };

  const scanRows = () => {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;

    root.querySelectorAll(ROW_SELECTOR).forEach(async (row) => {
      decorateSale(row);

      const handle = rowHandle(row);
      const variantId = rowVariantId(row);
      if (!handle || !variantId) return;

      if (await isViewOnlyVariant(handle, variantId)) decorateRow(row, handle);
    });
  };

  // Variant ids the app is about to add, minus the view-only ones. Ids without a settled verdict are
  // resolved through their row's handle; ids with no row on screen are let through unchanged.
  const filterAddable = async (items) => {
    const root = document.getElementById(ROOT_ID);
    const verdicts = await Promise.all(
      items.map(async (item) => {
        const key = String(item.id);
        if (viewOnlyVariants.has(key)) return !viewOnlyVariants.get(key);

        const row = root && root.querySelector(`#product_var${CSS.escape(key)}`);
        const handle = row ? rowHandle(row) : '';
        return handle ? !(await isViewOnlyVariant(handle, key)) : true;
      })
    );
    return items.filter((item, index) => verdicts[index]);
  };

  // Shaped like a /cart/add.js error so the app's own handlers show it as their error message.
  const blockedResponse = () => ({ status: 422, message: 'Cart Error', description: BLOCKED_MESSAGE });

  const wrapCartHelpers = () => {
    if (typeof window.addAllToCart === 'function' && !window.addAllToCart.themeViewOnly) {
      const addAllToCart = window.addAllToCart;
      window.addAllToCart = async (products) => {
        const addable = await filterAddable(Array.isArray(products) ? products : []);
        return addable.length ? addAllToCart(addable) : blockedResponse();
      };
      window.addAllToCart.themeViewOnly = true;
    }

    if (typeof window.addToCart === 'function' && !window.addToCart.themeViewOnly) {
      const addToCart = window.addToCart;
      window.addToCart = async (variantId, quantity) => {
        const addable = await filterAddable([{ id: variantId, quantity }]);
        return addable.length ? addToCart(variantId, quantity) : blockedResponse();
      };
      window.addToCart.themeViewOnly = true;
    }
  };

  // The drawer is re-rendered on every open, page change and list switch, so rows are watched for
  // rather than looked up once. The app mounts its root inside its own app-block wrapper some time
  // after load, so <body> is watched only until that root shows up, then just the root.
  let scanQueued = false;
  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      scanRows();
    });
  };

  const watchRoot = (root) => {
    new MutationObserver(queueScan).observe(root, { childList: true, subtree: true });
    queueScan();
  };

  const start = () => {
    // The app's scripts are deferred like this one, so its globals exist by DOMContentLoaded; the
    // click hook below re-checks in case the embed loads them later.
    wrapCartHelpers();

    const root = document.getElementById(ROOT_ID);
    if (root) {
      watchRoot(root);
      return;
    }

    const bodyObserver = new MutationObserver(() => {
      const mounted = document.getElementById(ROOT_ID);
      if (!mounted) return;
      bodyObserver.disconnect();
      watchRoot(mounted);
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest(`#${ROOT_ID} .add_to_cart, #${ROOT_ID} .addToCart-btn`)) wrapCartHelpers();
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}
